import { type ApiLocale, API_LOCALE } from '@/domain/locale'
import { formatWholeNumber } from '@/utils/kijiji-common/number'

export type GenerateSrpSeoTitleProps = {
  locale: ApiLocale
  searchKeywords?: string | null
  title?: string
  totalListings: number
  translation: (key: string, options: { totalCount: string; title?: string }) => string
}

const LISTING_COUNT_DISPLAY_MIN = 25

/**
 * Generates a SEO-friendly title based on the listings count and search parameters.
 *
 * @param locale The locale choosen by the user.
 * @param searchKeywords The search keywords used in the search.
 * @param title The base title derived from search metadata.
 * @param totalListings The total number of listings.
 * @param translation The translation function from next-i18next.
 *
 * @returns A SEO-optimized title string - Example: "1,000 ads for technician in Jobs in Canada".
 *
 * @example
 * const seoTitle = generateSeoTitle({
 *    locale: API_LOCALE.en_CA,
 *    title: 'Jobs in Canada',
 *    totalListings: 1000,
 *    keyword: 'technician',
 *    translation: t,
 * });
 */
export function generateSrpSeoTitle({
  locale,
  searchKeywords,
  title,
  totalListings,
  translation,
}: GenerateSrpSeoTitleProps): string {
  if (searchKeywords) {
    const isEnglish = locale === API_LOCALE.en_CA
    const adsNumber = formatWholeNumber(totalListings, isEnglish, false)

    let seoTitle = `${translation('meta.srp.title.seo', { totalCount: adsNumber, title })}`

    // If there are fewer than 25 listings, we don't need to show the count
    if (totalListings < LISTING_COUNT_DISPLAY_MIN) {
      seoTitle = title ? `${title}` : ''
    }

    return seoTitle
  }

  return title ?? ''
}
